import AsyncSelect from 'react-select/async';
import Dproject from '../../../models/Dproject';
import { Fragment, useState } from 'react';

// Custom styles for react-select to match Figma design
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '42px',
        height: '42px',
        borderRadius: '8px',
        borderColor: state.isFocused ? '#3b82f6' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
        fontSize: '1rem',
        '&:hover': {
            borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1'
        }
    }),
    valueContainer: (base) => ({
        ...base,
        height: '40px',
        padding: '0 12px'
    }),
    input: (base) => ({
        ...base,
        margin: '0',
        padding: '0'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '40px'
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: '1rem'
    }),
    singleValue: (base) => ({
        ...base,
        color: '#334155',
        fontSize: '1rem'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        fontSize: '1rem',
        padding: '10px 12px'
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    })
};

export const DprojectDropdown = ({ type, label, fieldName, bField, labelField, valueField, selectedValue, resource, handleChange, isSearchable, includeEmpty }) => {

    const defaultValue = type === 'bool' ? 0 : 'SP_EMPTY';

    const loadOptions = (value, callback) => {
        let query = {
            [`${bField}`]: { 'match_distinct': value ? value : defaultValue },
        }

        resource
        .select({ dprojects: [`${bField}`]})
        .where(query)
        .all()
        .then((res) => {
            const options = res.data
            .filter(e => (e[labelField] !== null) && (e[labelField] !== ''))
            .map(e => {
                if (type === 'bool') {
                    return {
                        label: (e[labelField] !== null) ? e[labelField].toString() : '',
                        value: e[valueField]
                    }
                } else {
                    return {
                        label: e[labelField],
                        value: e[valueField]
                    };
                }
            });

            if (includeEmpty) {
                options.unshift({ label: 'All', value: '' })
            }
            callback(options);
        });
    };

    return (
        <div className="filter-field">
            { label && <label>{label}</label> }
            <input type="text" name={fieldName}
                value={selectedValue && selectedValue.value}
                hidden readOnly
            />
            <AsyncSelect cacheOptions defaultOptions
                defaultValue={selectedValue}
                loadOptions={loadOptions}
                isSearchable={isSearchable}
                onChange={handleChange}
                styles={selectStyles}
                placeholder="All">
            </AsyncSelect>
        </div>
    );
};

Dproject.defaultProps = {
    isSearchable: true
}

const DprojectSearch = ({ model }) => {

    const [assignedTo, setAssignedTo] = useState({ label: model.assigned_to_eq, value: model.assigned_to_eq });
    const [assignedBy, setAssignedBy] = useState({ label: model.assigned_by_eq, value: model.assigned_by_eq });
    const [status, setStatus] = useState({ label: model.status_eq, value: model.status_eq });
    const [projectType, setProjectType] = useState({ label: model.project_type_eq, value: model.project_type_eq });

    const [schoolName, setSchoolName] = useState({ label: model.school_name_eq, value: model.school_name_eq });
    const [schoolRoute, setSchoolRoute] = useState({ label: model.school_route_eq, value: model.school_route_eq });
    const [invoiceStatus, setInvoiceStatus] = useState({ label: model.invoice_status_eq, value: model.invoice_status_eq });
    const [priority, setPriority] = useState({ label: model.priority_eq, value: model.priority_eq });

    const [districtName, setDistrictName] = useState({ label: model.district_name_eq, value: model.district_name_eq });

    return (
        <Fragment>
            {/* Row 1: School, District, Item Type, Invoice Status (4 columns) */}
            <div className="filter-row filter-row-4">
                <DprojectDropdown
                    label="School"
                    fieldName="q[school_name_eq]"
                    labelField="schoolName"
                    valueField="schoolName"
                    bField="school_name"
                    selectedValue={schoolName}
                    handleChange={setSchoolName}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="District"
                    fieldName="q[district_name_eq]"
                    labelField="districtName"
                    valueField="districtName"
                    bField="district_name"
                    selectedValue={districtName}
                    handleChange={setDistrictName}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Item Type"
                    fieldName="q[project_type_eq]"
                    labelField="projectType"
                    valueField="projectType"
                    bField="project_type"
                    selectedValue={projectType}
                    handleChange={setProjectType}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Invoice Status"
                    fieldName="q[invoice_status_eq]"
                    labelField="invoiceStatus"
                    valueField="invoiceStatus"
                    bField="invoice_status"
                    selectedValue={invoiceStatus}
                    handleChange={setInvoiceStatus}
                    resource={Dproject}
                    includeEmpty
                />
            </div>
            {/* Row 2: Assigned To, Assigned By, Status, Priority, Route (5 columns) */}
            <div className="filter-row filter-row-5" style={{ marginTop: '16px' }}>
                <DprojectDropdown
                    label="Assigned To"
                    fieldName="q[assigned_to_eq]"
                    labelField="assignedTo"
                    valueField="assignedTo"
                    bField="assigned_to"
                    selectedValue={assignedTo}
                    handleChange={setAssignedTo}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Assigned By"
                    fieldName="q[assigned_by_eq]"
                    labelField="assignedBy"
                    valueField="assignedBy"
                    bField="assigned_by"
                    selectedValue={assignedBy}
                    handleChange={setAssignedBy}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Status"
                    fieldName="q[status_eq]"
                    labelField="status"
                    valueField="status"
                    bField="status"
                    selectedValue={status}
                    handleChange={setStatus}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Priority"
                    fieldName="q[priority_eq]"
                    type="bool"
                    labelField="priority"
                    valueField="priority"
                    bField="priority"
                    isSearchable={false}
                    selectedValue={priority}
                    handleChange={setPriority}
                    resource={Dproject}
                    includeEmpty
                />
                <DprojectDropdown
                    label="Route"
                    fieldName="q[school_route_eq]"
                    labelField="schoolRoute"
                    valueField="schoolRoute"
                    bField="school_route"
                    selectedValue={schoolRoute}
                    handleChange={setSchoolRoute}
                    resource={Dproject}
                    includeEmpty
                />
            </div>
        </Fragment>
    );
};

export default DprojectSearch;
